import { useAuth } from '../hooks/useAuth';

/**
 * Check if the current user has a specific widget permission
 * @param {string} widgetPermission - The widget permission to check
 * @returns {boolean} - Whether the user has the permission
 */
export const useWidgetPermission = (widgetPermission) => {
  const { user, hasPermission } = useAuth();
  
  if (!user || !widgetPermission) {
    return false;
  }
  
  return hasPermission(widgetPermission);
};

/**
 * Check if the user has permission to view cost data
 * @returns {boolean} - Whether the user has permission to view cost data
 */
export const useHasCostPermission = () => {
  const { hasPermission } = useAuth();
  return hasPermission(WIDGET_PERMISSIONS['production-cost']) && 
         hasPermission(WIDGET_PERMISSIONS['client-cost-analysis']);
};

/**
 * Higher-order component to conditionally render a widget based on permissions
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The widget content to conditionally render
 * @param {string} props.permission - The permission required to view this widget
 * @param {React.ReactNode} props.fallback - Optional fallback UI when permission is denied
 * @returns {React.ReactNode} - The widget if permitted, fallback or null otherwise
 */
export const PermissionWidget = ({ children, permission, fallback = null }) => {
  const { hasPermission } = useAuth();
  
  if (!permission) {
    return children; // If no permission is specified, show the widget
  }
  
  return hasPermission(permission) ? children : fallback;
};

/**
 * Collection of widget permissions used in the application
 */
export const WIDGET_PERMISSIONS = {
  // Dashboard widgets
  'rsyslog-data': 'WIDGET_RSYSLOG_DATA',
  'production-cost': 'WIDGET_PRODUCTION_COST',
  
  // Client page widgets
  'client-cost-analysis': 'WIDGET_CLIENT_COST_ANALYSIS',
  'client-cost-breakdown': 'WIDGET_CLIENT_COST_BREAKDOWN',
  'compute-cost': 'WIDGET_COMPUTE_COST',
  'boot-volume-cost': 'WIDGET_BOOT_VOLUME_COST',
  'block-volume-cost': 'WIDGET_BLOCK_VOLUME_COST',
  'client-data-size': 'WIDGET_CLIENT_DATA_SIZE',
  'client-threshold': 'WIDGET_CLIENT_THRESHOLD',
  'cost-per-gb': 'WIDGET_PRODUCTION_COST',
  'total-cost': 'WIDGET_PRODUCTION_COST',
}; 